import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  AvatarGroup,
  Chip,
  Badge,
  IconButton,
  Divider,
  Button,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  CheckCircle as ApprovedIcon,
  Cancel as PendingIcon,
  Close as CloseIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  WatchLater as WatchLaterIcon,
  Close as RejectedIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import RaceChip from './RaceChip';

const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

const CancelMatchCard = ({ match, usersMap, onClick }) => {
  const navigate = useNavigate();
  const isRejected = match.approval_status === 'rejected';
  const isCancelled = match.approval_status === 'cancelled';

  // Función para obtener los nombres de usuario a partir de los jugadores
  const getUsernamesFromPlayers = (players) => {
    if (!Array.isArray(players)) return [];
    return players.map(player => {
      const username = usersMap[player.user_id];
      return {
        id: player.user_id,
        name: username || player.user_id
      };
    });
  };

  const getPlayerDecks = (teamNumber) => {
    const team = teamNumber === 1 ? match.team1 : match.team2;
    if (!team?.decks || !Array.isArray(team.decks)) {
      return [];
    }
    
    return team.decks.map(deck => ({
      name: deck.deck_name || 'Mazo sin nombre',
      race: deck.deck_raza || 'Desconocida'
    }));
  };

  const getPlayerApprovalStatus = (playerId, teamNumber) => {
    try {
      const team = teamNumber === 1 ? match.team1 : match.team2;
      if (!team?.players) return false;
      
      const player = team.players.find(p => p.user_id === playerId);
      return player?.approval_status;
    } catch (error) {
      console.warn('Error checking player approval status:', error);
      return false;
    }
  };

  const team1Players = getUsernamesFromPlayers(match.team1?.players || []);
  const team2Players = getUsernamesFromPlayers(match.team2?.players || []);
  const isTeam1Winner = match.winning_team === 'team1';

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick(e, match);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 1.8,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isRejected ? 'rgba(244, 67, 54, 0.5)' : 'rgba(158, 158, 158, 0.5)',
        bgcolor: isRejected ? 'rgba(244, 67, 54, 0.05)' : 'rgba(158, 158, 158, 0.05)',
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          borderColor: isRejected ? 'rgba(244, 67, 54, 0.8)' : 'rgba(158, 158, 158, 0.8)',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)'
        }
      }}
    >
      <Stack spacing={1.5}>
        {/* Match Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          gap: 0.8,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
          }}>
            <ScheduleIcon sx={{ 
              color: 'text.secondary',
              fontSize: '1.26rem'
            }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.84rem'
              }} 
              noWrap
            >
              {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.8,
            ml: 'auto'
          }}>
            <Chip
              icon={<GameIcon sx={{ 
                color: 'text.secondary',
                fontSize: '1.26rem'
              }} />}
              label={match.match_mode}
              size="small"
              sx={{ 
                height: '23px',
                bgcolor: 'action.hover',
                color: 'text.primary',
                '& .MuiChip-label': { 
                  fontSize: '0.735rem',
                  px: 0.8
                }
              }}
            />
            <Chip
              icon={<TrophyIcon sx={{ 
                color: getTierColor(match.tier).text,
                fontSize: '1.2rem'
              }} />}
              label={match.tier}
              size="small"
              sx={{ 
                height: '22px',
                bgcolor: getTierColor(match.tier).bg,
                color: getTierColor(match.tier).text,
                '& .MuiChip-label': { 
                  fontSize: '0.7rem',
                  px: 0.8
                }
              }}
            />
          </Box>
        </Box>

        {/* Teams Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 0.8
        }}>
          {/* Team 1 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center',
                  flexShrink: 0
                }}
              >
                {match.team1?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 0,
                width: '100%'
              }}>
                <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {team1Players.map((player, idx) => {
                      const status = getPlayerApprovalStatus(player.id, 1);
                      const isRejected = status === 'rejected';
                      const isCancelled = status === 'cancelled';
                      
                      return (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {isRejected ? (
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              ) : isCancelled ? (
                                <BlockIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'grey.600',
                                  }}
                                />
                              ) : null}
                            </Box>
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: 'grey.700',
                              border: '2px solid',
                              borderColor: isRejected ? '#f44336' : isCancelled ? 'grey.600' : 'grey.500',
                              color: 'white'
                            }}
                          >
                            {player.name[0]?.toUpperCase()}
                          </Avatar>
                        </Badge>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.secondary'
                      }}
                    >
                      {team1Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team1Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {(() => {
                      const decks = getPlayerDecks(1);
                      return decks.map((deck, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={deck.race} size="small" />
                        </Box>
                      ));
                    })()}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>

          {/* VS */}
          <Typography 
            variant="body1" 
            sx={{ 
              mx: 1,
              fontSize: { xs: '0.86rem', sm: '0.99rem' },
              color: 'text.secondary',
              fontWeight: 'bold'
            }}
          >
            VS
          </Typography>

          {/* Team 2 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              flexDirection: 'row-reverse',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: '60px'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'text.secondary',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    textAlign: 'center'
                  }}
                >
                  {match.team2?.score || 0}
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
              }}>
                <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}>
                    {team2Players.map((player, idx) => {
                      const status = getPlayerApprovalStatus(player.id, 2);
                      const isRejected = status === 'rejected';
                      const isCancelled = status === 'cancelled';
                      
                      return (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {isRejected ? (
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              ) : isCancelled ? (
                                <BlockIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'grey.600',
                                  }}
                                />
                              ) : null}
                            </Box>
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: 'grey.700',
                              border: '2px solid',
                              borderColor: isRejected ? '#f44336' : isCancelled ? 'grey.600' : 'grey.500',
                              color: 'white'
                            }}
                          >
                            {player.name[0]?.toUpperCase()}
                          </Avatar>
                        </Badge>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, width: '100%' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.secondary'
                      }}
                    >
                      {team2Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team2Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {(() => {
                      const decks = getPlayerDecks(2);
                      return decks.map((deck, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <RaceChip race={deck.race} size="small" />
                        </Box>
                      ));
                    })()}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Match Info - Highlights */}
        {(() => {
          try {
            if (!match.highlights || !Array.isArray(match.highlights) || match.highlights.length === 0) {
              return null;
            }
            
            return (
              <Stack 
                direction="row" 
                spacing={0.4}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ gap: 0.4 }}
              >
                {match.highlights.map((highlight, index) => {
                  const highlightText = highlight.type || highlight;
                  
                  return (
                    <Chip
                      key={index}
                      icon={getHighlightIcon(highlightText)}
                      label={highlightText}
                      size="small"
                      sx={{ 
                        height: '22px',
                        bgcolor: '#0277bd',
                        color: 'white',
                        '& .MuiChip-label': { 
                          fontSize: '0.7rem',
                          px: 0.8
                        },
                        '& .MuiChip-icon': {
                          color: 'white',
                          fontSize: '1.2rem'
                        }
                      }}
                    />
                  );
                })}
              </Stack>
            );
          } catch (error) {
            console.warn('Error processing highlights:', error);
            return null;
          }
        })()}
      </Stack>
    </Paper>
  );
};

export default CancelMatchCard; 